import React, { useEffect, createRef, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./invoicePayment.scss";
import PaymentPopup from "./paymentPopup";
import {
  paymentSelector,
  resetPayment,
  savePayment,
  setPaymentHiddenFormSubmitted,
  setPaymentTimedOut,
} from "../../../slices/payment/paymentSlice";
import PaymentState from "../../../slices/payment/PaymentState";
import { PaymentResult } from "app/data/payment/PaymentResult";
import { usePaymentBlocked } from "features/shipments/shipment-details/hooks/usePaymentBlocked";

declare global {
  interface Window {
    AuthorizeNetIFrame: any;
  }
}

export interface InvoicePaymentProps {
  invoiceNumber: string;
}

const PAYMENT_TIMEOUT_MS = 5 * 60 * 1000; 

let iFrameInited = false;

const InvoicePayment: React.FC<InvoicePaymentProps> = ({ invoiceNumber }) => {
  const payment: PaymentState = useSelector(paymentSelector);
  const dispatch = useDispatch();
  const formRef = createRef<HTMLFormElement>();
  const [, setBlocked] = usePaymentBlocked(invoiceNumber);

  const processedTransIdsRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Timer helpers ───────────────────────────────────────────────────────────
  const clearPaymentTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPaymentTimer = useCallback(() => {
    clearPaymentTimer();

    timerRef.current = setTimeout(() => {
      console.warn(`[Payment] Session timed out for invoice ${invoiceNumber}`);

      // Persist first — if dispatch somehow re-renders before localStorage write,
      // the mount check above would miss it
      dispatch(setPaymentTimedOut()); // closes popup, sets timedOut: true
      setBlocked();

    }, PAYMENT_TIMEOUT_MS);
  }, [clearPaymentTimer, dispatch, invoiceNumber, setBlocked]);
  
  useEffect(() => {
    return () => clearPaymentTimer();
  }, [clearPaymentTimer]);

  // ── Submit hidden form + start timer when token is ready ───────────────────
  useEffect(() => {
    if (!payment.hiddenFormSubmitted && payment.tokenInfo?.token) {
      formRef.current?.submit();
      dispatch(setPaymentHiddenFormSubmitted());
      startPaymentTimer(); // clock starts the moment the iframe loads
    }
  }, [
    dispatch,
    formRef,
    payment.hiddenFormSubmitted,
    payment.tokenInfo,
    startPaymentTimer,
  ]);

  // ── Iframe communication ────────────────────────────────────────────────────
  useEffect(() => {
    if (iFrameInited) return;

    if (!window.AuthorizeNetIFrame) window.AuthorizeNetIFrame = {};

    window.AuthorizeNetIFrame.onReceiveCommunication = (
      querystr: string,
      _parent: any
    ) => {
      const params = parseQueryString(querystr);
      console.log("onReceiveCommunication >>>> params", params);

      switch (params.action) {
        case "successfulSave":
          break;

        case "cancel":
          // User cancelled — clear timer, allow them to retry normally
          clearPaymentTimer();
          dispatch(resetPayment());
          break;

        case "resizeWindow":
          break;

        case "transactResponse": {
          try {
            if (!params.response) return;

            const response = JSON.parse(params.response);
            const transId = response?.transId;
            if (!transId) return;

            if (processedTransIdsRef.current.has(transId)) {
              console.warn("[Payment] Duplicate transactResponse ignored:", transId);
              return;
            }
            processedTransIdsRef.current.add(transId);

            // Success — stop timer, wipe any stale timeout flag, process payment
            clearPaymentTimer();
            dispatch(savePayment(response as PaymentResult));
          } catch (err) {
            console.error("[Payment] Failed to parse transactResponse:", err);
          }
          break;
        }
      }
    };

    iFrameInited = true;
  }, [dispatch, clearPaymentTimer, payment?.tokenInfo?.refId]);

  const parseQueryString = (str: string): any => {
    const vars: any = [];
    const arr = str.split("&");
    for (const item of arr) {
      const pair = item.split("=");
      vars.push(pair[0]);
      vars[pair[0]] = unescape(pair[1]);
    }
    return vars;
  };

  return (
    <>
      <PaymentPopup />
      {payment.showPopup && (
        <form
          ref={formRef}
          method="post"
          action={process.env.REACT_APP_AUTHORIZE_FORM_POST_URL}
          id="formAuthorizeNetPopup"
          name="formAuthorizeNetPopup"
          target="iframe-authorize-net"
          style={{ display: "none" }}
        >
          <input
            type="hidden"
            id="popupToken"
            name="token"
            value={payment.tokenInfo?.token ?? ""}
          />
        </form>
      )}
    </>
  );
};

export default InvoicePayment;